import cn from 'classnames';
import Image from 'next/future/image';
import { Dispatch, SetStateAction } from 'react';
import { CtaButton } from '@/components/Cta/Cta';
import i18n from '@/settings/i18n';
import settings, { priceFormater } from '@/settings';
import cartAccount from '@/public/icons/account/cart.svg';
import cartSummaryArrow from '@/public/icons/cart/cartSummaryArrow.svg';
import { warrantyUpsellT } from '@/lib/reebelocare/types';

const t = i18n.init();

type Props = {
  totalCart: number;
  isLoading: boolean;
  cartItemsCount: number;
  isItemsVisible: boolean;
  totalCompareprice: number;
  onSummaryClick: () => void;
  onCheckoutClick: () => void;
  warrantyUpsellList: warrantyUpsellT[];
  setShowWarrantyUpsell: Dispatch<SetStateAction<boolean>>;
  setIsCheckingOut: Dispatch<SetStateAction<boolean>>;
  isCheckingOut: boolean;
  warrantyUpsellShown: boolean;
  setWarrantyUpsellShown: Dispatch<SetStateAction<boolean>>;
  includesExpressShipping: boolean;
};

export default function CartInfo({
  isLoading,
  totalCart,
  cartItemsCount,
  isItemsVisible,
  onSummaryClick,
  onCheckoutClick,
  totalCompareprice,
  warrantyUpsellList,
  setShowWarrantyUpsell,
  setIsCheckingOut,
  isCheckingOut,
  warrantyUpsellShown,
  setWarrantyUpsellShown,
  includesExpressShipping,
}: Props) {
  if (window != null) {
    // to fix Safari & other browser back button state not resetting issue
    window.onpopstate = () => {
      setIsCheckingOut(false);
    };

    window.onpageshow = () => {
      setIsCheckingOut(false);
    };
  }

  const generateMoreInfo = () => {
    if (['reebelo-us', 'reebelo-ca'].includes(settings.store)) {
      return `Tax calculated at checkout.${
        !includesExpressShipping ? ' Free shipping.' : ''
      }`;
    }

    return `Tax included.${!includesExpressShipping ? ' Free shipping.' : ''}`;
  };

  const moreInfo = generateMoreInfo();

  return (
    <div className="flex w-full flex-wrap">
      <div
        className="flex w-full cursor-pointer select-none flex-nowrap items-center border-none border-gray-500 px-0 py-5 xl:hidden xl:border-b xl:p-5 xl:px-5"
        onClick={onSummaryClick}
      >
        <div className="h-[20px] w-[25px]">
          <Image height={20} width={25} alt="cartAccount" src={cartAccount} />
        </div>
        <h3
          className="mx-2 text-sm font-bold"
          id={
            isItemsVisible ? `e2e-cart-hide-summary` : `e2e-cart-show-summary`
          }
        >
          {isItemsVisible ? t`Hide cart summary` : t`View cart summary`}
        </h3>
        <span
          className={cn('flex h-[20px] w-[11px] justify-end', {
            'rotate-180': isItemsVisible,
            'rotate-0': !isItemsVisible,
          })}
        >
          <Image
            alt="hand"
            src={cartSummaryArrow}
            className="h-full w-full object-fill"
          />
        </span>
        <span className="ml-auto justify-self-end text-sm">
          {t`${{ cartItemsCount }} items`}
        </span>
      </div>
      <div className="fixed bottom-0 left-0 z-20 flex w-full flex-wrap justify-between border-b border-gray-500 bg-white p-5 xl:static xl:bg-transparent">
        <div className="flex w-full items-center">
          <h5 className="text-sm font-bold text-gray-700 sm:text-lg">
            {t`Total`}
            <p className="-mt-1 w-full text-sm font-normal text-gray-900">
              {moreInfo}
            </p>
          </h5>
          <div className="ml-auto text-right">
            <p
              className="text-sm font-bold text-gray-700 sm:text-lg"
              id="e2e-cart-total"
            >
              {priceFormater.long(totalCart)}
            </p>
            {totalCompareprice > totalCart && (
              <p className="-mt-1 text-sm text-gray-900 line-through xl:text-[14px]">
                {priceFormater.long(totalCompareprice)}
              </p>
            )}
          </div>
        </div>
        <div className="mt-2 w-full">
          <CtaButton
            variant="dark"
            loading={isLoading || isCheckingOut}
            onClick={() => {
              if (warrantyUpsellList.length > 0 && !warrantyUpsellShown) {
                setWarrantyUpsellShown(true);
                setShowWarrantyUpsell(true);
              } else {
                setIsCheckingOut(true);
                onCheckoutClick();
              }
            }}
            id="e2e-cart-checkout-btn"
            className="my-3 block w-full !p-3 font-bold"
          >
            {t`Checkout`}
          </CtaButton>
        </div>
      </div>
    </div>
  );
}
